"use client";

import { useState } from "react";
import Image from "next/image";
import { GROQImage } from "@/sanity/lib/definitions";
import { IMuxVideoPlayer } from "@/sanity/globals/VideoPlayer";
import { SanityVideoPlayer } from "@/components/ui/SanityVideoPlayer";
import { FadeIn } from "@/components/global/utils/FadeIn";
import { cn } from "@/lib/utils";
import playIcon from "@/public/courseDetail/trailer-play.svg";
import wceMark from "@/public/courseDetail/wce-mark.svg";

interface TrailerSectionProps {
  showPlaceholder?: boolean;
  poster?: GROQImage;
  duration?: string;
  video?: IMuxVideoPlayer;
  /** Overrides the section spacing for pages other than the course detail (e.g. the Mentor page). */
  className?: string;
}

// Course Detail trailer — Figma 3038:26568 (desktop) / 3135:1467 (mobile).
// A 1200×633 rounded frame: poster, optional centred play button, JetBrains
// Mono duration chip bottom-left, WCE mark bottom-right. When the Studio has a
// video, clicking play swaps the poster for the Mux player.
export default function TrailerSection({ poster, duration, video, className, showPlaceholder = false }: TrailerSectionProps) {
  const [playing, setPlaying] = useState(false);

  if (!poster && !video && !showPlaceholder) return null;

  const hasVideo = Boolean(video?.embed ? video?.embedUrl : video?.muxVideo?.playbackId);

  return (
    <section
      id="trailer"
      className={cn(
        "flex w-screen scroll-mt-[8rem] flex-col items-center bg-black px-[2rem] pb-[10rem] lg:scroll-mt-0 lg:px-0 lg:pb-[20rem]",
        className,
      )}>
      <FadeIn className="w-full lg:w-[150rem]">
        <div className="relative h-[26.125rem] w-[49.75rem] max-w-none overflow-clip rounded-[0.75rem] bg-white/[0.04] lg:h-[79.125rem] lg:w-[150rem] lg:rounded-[1.75rem]">
          {playing && hasVideo ? (
            <SanityVideoPlayer
              playerData={video!}
              poster={poster?.src ?? video?.thumbnail?.src}
              playsinline
              autoPlay
              className="h-full w-full"
            />
          ) : (
            <>
              {poster && (
                <Image
                  src={poster.src}
                  alt=""
                  fill
                  quality={100}
                  sizes="(max-width: 992px) 100vw, 150rem"
                  placeholder={poster.lqip ? "blur" : undefined}
                  blurDataURL={poster.lqip}
                  className="object-cover"
                />
              )}

              {hasVideo && (
                <button
                  type="button"
                  aria-label="Play trailer"
                  onClick={() => setPlaying(true)}
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 cursor-pointer transition hover:scale-105">
                  <Image
                    src={playIcon}
                    alt=""
                    className="h-[5.25rem] w-[5.25rem] lg:h-[9rem] lg:w-[9rem]"
                  />
                </button>
              )}

              <div className="absolute bottom-0 left-0 flex w-full items-end justify-between p-[1.5rem] lg:p-[3rem]">
                {duration ? (
                  <span className="flex items-center justify-center rounded-[0.375rem] bg-white p-[0.6875rem] font-jetbrainsmono text-[1.375rem] leading-none font-medium text-black/80 uppercase lg:rounded-[0.5rem] lg:p-[1rem] lg:text-[2rem]">
                    {duration}
                  </span>
                ) : (
                  <span />
                )}
                <Image
                  src={wceMark}
                  alt=""
                  className="h-[2.75rem] w-[2.625rem] lg:h-[4rem] lg:w-[3.875rem]"
                />
              </div>
            </>
          )}
        </div>
      </FadeIn>
    </section>
  );
}
